import { helpers } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { TypeCampaignEnum } from '@/types';
import type { FlowOperatorMetadata, ValidationResult } from '@/types/flow-types';
import { store } from '@/store';
import { i18n } from '@/i18n';
import { hasAtLeastOneSegmentationRule } from '@/composables/segmentationEditor';
import Form from './Form.vue';
import { canDeleteTrigger } from '@/components/flows/operators/registry';

export interface AudienceSetupData {
  lists: number[];
  segment_the_list: string;
  segmentation_config: any;
  /** When '1', target all shop contacts; lists must be empty and segmentation is required. */
  all_contacts?: string;
}

function normalizeAllContacts(value: unknown): boolean {
  return value === '1' || value === 1 || value === true;
}

export function createDefaultData(data: Partial<AudienceSetupData> = {}): AudienceSetupData {
  const allContacts = normalizeAllContacts(data.all_contacts);
  return {
    lists: allContacts ? [] : (data.lists ?? []),
    // Segmentation is now optional in both modes — keep the user's choice as-is.
    segment_the_list: data.segment_the_list ?? '0',
    segmentation_config: data.segmentation_config ?? [],
    all_contacts: allContacts ? '1' : (data.all_contacts === undefined || data.all_contacts === null ? '0' : String(data.all_contacts)),
  };
}

const AudienceSetup: FlowOperatorMetadata<AudienceSetupData> = {
  id: 'boxaudiencesetup',
  kind: 'trigger',
  label: 'automatedScenarios.triggers.boxAudienceSetup.label',
  icon: 'fa-mail-bulk',
  hasConfiguration: true,
  availableIn: [TypeCampaignEnum.BULK],

  incompatibilities: {
    [TypeCampaignEnum.BULK]: {
      nextOperators: ['boxaudiencesetup'],
    },
  },

  Form,

  defaultData: createDefaultData,

  outputs: () => 1,

  getDisplayLabel: (data: AudienceSetupData, t) => {
    if (normalizeAllContacts(data.all_contacts)) {
      return t('automatedScenarios.triggers.boxAudienceSetup.dynamicLabelAllContacts');
    }

    // Handle different formats: array, JSON string "[1,2,3]", or comma-separated string
    let count = 0;
    let lists = data.lists as unknown;
    
    // If it's a JSON string like "[1530]", parse it
    if (typeof lists === 'string') {
      try {
        lists = JSON.parse(lists);
      } catch {
        // Not valid JSON, keep as string
      }
    }
    
    if (Array.isArray(lists)) {
      count = lists.length;
    }
    
    if (count === 0) return null; // Use default label
    
    // Use singular or plural translation key
    const labelKey = count === 1 
      ? 'automatedScenarios.triggers.boxAudienceSetup.dynamicLabelSingular'
      : 'automatedScenarios.triggers.boxAudienceSetup.dynamicLabelPlural';
    return t(labelKey, { number: count });
  },

  async preValidate(data: AudienceSetupData): Promise<ValidationResult> {
    let success = true;
    if (data.segment_the_list === '1') {
      await store.dispatch('segmentationsEditor/validate');
      const getHasError = store.getters['segmentationsEditor/getHasError'];
      success = !getHasError;
    }
    return { success };
  },

  async validate(data: AudienceSetupData): Promise<ValidationResult> {
    const allContacts = normalizeAllContacts(data.all_contacts);
    // Segmentation is optional in both modes; only validate the segmentation
    // config when the user explicitly answered "Yes" to segmenting.
    const requiredSegConfig = data.segment_the_list === '1';

    const allRules: Record<string, any> = {
      lists: {
        required: helpers.withMessage(
          i18n.global.t('customValidator.required'),
          (value: unknown) => {
            if (allContacts) return true;
            let lists = value as unknown;
            if (typeof lists === 'string') {
              try {
                lists = JSON.parse(lists);
              } catch {
                return false;
              }
            }
            return Array.isArray(lists) && lists.length > 0;
          },
        ),
      },
      segment_the_list: {
        required: helpers.withMessage(
          i18n.global.t('customValidator.required'),
          (value: unknown) => value !== null && value !== undefined && value !== '',
        ),
      },
    };

    if (requiredSegConfig) {
      allRules.segmentation_config = {
        required: helpers.withMessage(
          i18n.global.t('automatedScenarios.triggers.boxAudienceSetup.segmentationMinOneRule'),
          (value: unknown) => hasAtLeastOneSegmentationRule(value),
        ),
      };
    }

    const v$ = useVuelidate(allRules, data);
    const success = await v$.value.$validate();
    return {
      success,
      validate: v$,
    };
  },

  canBeDeleted(operatorId: string, flow: any): Promise<boolean | string> {
    return canDeleteTrigger(flow, operatorId);
  },
};

export default AudienceSetup;
